import React, { memo, useEffect, useCallback, useMemo } from 'react';
import { FormBase, InputField, Validator, SelectField } from '../../components';
import { Input, Row } from 'reactstrap';
import BasicRowColGrid from '../../utility/BasicRowColGrid';
import strings from '../../globalization';
import { reduxForm, Field, initialize, change } from 'redux-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  trackingSystemContactForm,
  TRACKING_SYSTEM_CONTACT_FORM
} from '../../redux/actions';
import utility from '../../utility';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Renders a styled checkbox input for use with form libraries (e.g., Redux Form, React Final Form).
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.input - Input props provided by the form library (contains `value`, `onChange`, etc.).
 * @param {Object} props.meta - Metadata provided by the form library (contains `touched`, `error`, etc.).
 * @param {...Object} rest - Any additional props to be passed to the underlying <Input> component.
 * @returns {JSX.Element} A checkbox input element controlled by form state.
 *
 * @example
 * <Field
 *   name="IsActive"
 *   component={RenderCheckbox}
 *   type="checkbox"
 * />
 */
const RenderCheckbox = ({ input, meta, ...rest }) => (
  <Input
    {...input}
    {...rest}
    type="checkbox"
    checked={!!input.value}
    className="ms-0"
    style={{
      margin: 0,
      width: '1.25rem',
      height: '1.25rem',
      cursor: 'pointer'
    }}
  />
);

const TrackingSystemContactForm = memo(props => {
  const { params } = props.match;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const trackingSystemId = queryParams.get('trackingSystemId');
  const returnUrl = queryParams.get('returnUrl');
  const { data: initialValues = {}, success, info, err } = useSelector(
    state => state.trackingSystemContactFormData.data
  );
  const countriesData = useSelector(state => state.comboTypes.CountryCode);
  const shouldGoBack = useMemo(
    () =>
      success &&
      [
        'RecordSavedSuccessfully',
        'RecordUpdatedSuccessfully',
        'DeleteSuccess'
      ].includes(info),
    [success, info]
  );
  useEffect(() => {
    if (shouldGoBack) {
      if (returnUrl) {
        history.push(returnUrl);
      } else if (trackingSystemId) {
        history.push(`/trackingSystem/${trackingSystemId}`);
      } else {
        history.push('/trackingSystem');
      }
    }
  }, [shouldGoBack, trackingSystemId, returnUrl]);
  useEffect(() => {
    dispatch(
      initialize('TrackingSystemContact', { IsActive: true, ...initialValues })
    );
  }, [dispatch, initialValues]);
  useEffect(() => {
    dispatch(
      trackingSystemContactForm({
        action: 'load',
        _id: params.id,
        comboType: ['CountryCode']
      })
    );
  }, [params.id]);
  const onSubmit = useCallback(
    values => {
      dispatch(
        trackingSystemContactForm({
          action: params.id.toLowerCase() === 'new' ? 'add' : 'update',
          ...values,
          tableName: 'TrackingSystemContact',
          trackingSystemId: trackingSystemId
        })
      );
    },
    [dispatch, params.id, trackingSystemId]
  );
  const countries =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      countriesData
    ) || [];
  return (
    <FormBase
      {...props}
      initialValues={initialValues}
      err={err}
      isDark={props.isDark}
      title={strings.TrackingSystemContacts}
      subTitle={initialValues.ContactName}
      onSubmit={onSubmit}
      actionInfo={{
        action: trackingSystemContactForm,
        actionName: TRACKING_SYSTEM_CONTACT_FORM
      }}
      onFormCancel={() => {
        if (returnUrl) {
          history.push(returnUrl);
        } else if (trackingSystemId) {
          history.push(`/trackingSystem/${trackingSystemId}`);
        } else {
          history.push('/trackingSystem');
        }
      }}
    >
      <Row sm="12" md="12" lg="12" style={{ width: '100%' }}>
        <BasicRowColGrid>
          <label htmlFor="contactName">{strings.ContactName}</label>
          <Field
            name="ContactName"
            component={InputField}
            type="text"
            placeholder={strings.ContactName}
            maxLength={50}
            validate={Validator.required}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="companyName">{strings.CompanyName}</label>
          <Field
            name="CompanyName"
            component={InputField}
            placeholder={strings.CompanyName}
            type="text"
            maxLength={50}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="contactEmail">{strings.ContactEmail}</label>
          <Field
            name="ContactEmail"
            component={InputField}
            type="text"
            placeholder={strings.ContactEmail}
            maxLength={50}
            validate={[Validator.email]}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="companyAddress1">{strings.CompanyAddress1}</label>
          <Field
            name="CompanyAddress1"
            component={InputField}
            type="text"
            placeholder={strings.CompanyAddress1}
            maxLength={50}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="companyAddress2">{strings.CompanyAddress2}</label>
          <Field
            name="CompanyAddress2"
            component={InputField}
            type="text"
            placeholder={strings.CompanyAddress2}
            maxLength={50}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="city">{strings.City}</label>
          <Field
            name="City"
            component={InputField}
            type="text"
            placeholder={strings.City}
            maxLength={50}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="state">{strings.State}</label>
          <Field
            name="State"
            component={InputField}
            type="text"
            placeholder={strings.State}
            maxLength={50}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="postalCode">{strings.PostalCode}</label>
          <Field
            name="PostalCode"
            component={InputField}
            type="text"
            placeholder={strings.PostalCode}
            maxLength={50}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="phoneNumber">{strings.PhoneNumber}</label>
          <Field
            name="PhoneNumber"
            component={InputField}
            placeholder={strings.PhoneNumber}
            maxLength={15}
            normalize={value =>
              value ? value.replace(/[^\d\s+\-()]/g, '') : ''
            }
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="countryCode">{strings.CountryCode}</label>
          <Field
            name="CountryCode"
            component={SelectField}
            options={countries}
            maxLength={50}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          <label htmlFor="IsActive">{strings.Active}</label>
          <Field name="IsActive" component={RenderCheckbox} type="checkbox" />
        </BasicRowColGrid>
      </Row>
    </FormBase>
  );
});
const TrackingSystemContactReduxForm = reduxForm({
  form: 'TrackingSystemContact',
  enableReinitialize: false,
  keepValues: true
})(TrackingSystemContactForm);
const mapDispatchToProps = dispatch => ({
  change: (field, value) =>
    dispatch(change('TrackingSystemContact', field, value))
});
const mapStateToProps = state => {
  const { TrackingSystemContact } = state.form;
  const {
    data,
    success,
    info,
    CustomError,
    err
  } = state.trackingSystemContactFormData.data;
  const { isDark } = state.settingData;
  return {
    initialValues: data,
    comboType: state.comboTypes,
    isDark: isDark,
    formValues: TrackingSystemContact ? TrackingSystemContact.values : null,
    shouldGoBack:
      success &&
      (info === 'RecordSavedSuccessfully' ||
        info === 'RecordUpdatedSuccessfully' ||
        info === 'DeleteSuccess'),
    loginUserData: state.loginData.data,
    CustomError,
    err
  };
};
const TrackingSystemContactFormModule = connect(
  mapStateToProps,
  mapDispatchToProps
)(TrackingSystemContactReduxForm);
export default props => {
  return <TrackingSystemContactFormModule {...props} />;
};
